import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useSignatureJournalCheck, useSignatureRequest } from '../../hooks/useSignatures'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { SignatureRequestCard } from './SignatureRequestsPanel'

/**
 * One signature request (spec 2026-10-05-05): the follow-up card, the source
 * and sealed documents, and the evidence journal with its hash chain checked.
 */
export function SignatureRequestPage() {
    const { org, uid } = useParams({ from: '/$org/signatures/$uid' })
    const { t } = useTranslation('signatures')
    const { formatDate } = useFormatters()
    const { data: request, isLoading, error } = useSignatureRequest(org, uid)
    const chain = useSignatureJournalCheck(org, uid, !!request)
    useBreadcrumbs([{ title: t('title'), path: `/${org}/signatures` }, { title: request?.title ?? '' }])

    if (isLoading) return <p className="text-sm text-muted-foreground">{t('loading')}</p>
    if (error || !request) return <p className="text-sm text-muted-foreground" data-testid="signature-request-not-found">{t('not_found')}</p>

    return (
        <div className="space-y-6" data-testid="signature-request-page">
            <PageHeader icon={SECTION_ICONS.signatures} title={request.title} description={t(`request_status.${request.status}`)} />
            <ul className="rounded-lg border p-4">
                <SignatureRequestCard org={org} request={request} />
            </ul>
            <section className="space-y-1 text-sm">
                <p>
                    {t('detail.source')}{' '}
                    <Link to="/$org/documents/$docUid" params={{ org, docUid: request.document_uid }} className="text-primary hover:underline" data-testid="signature-request-source">
                        {request.document_name}
                    </Link>
                </p>
                <p className="break-all text-xs text-muted-foreground">SHA-256 {request.document_sha256}</p>
                {request.signed_document_uid && (
                    <p>
                        {t('detail.sealed')}{' '}
                        <Link to="/$org/documents/$docUid" params={{ org, docUid: request.signed_document_uid }} className="text-primary hover:underline" data-testid="signature-request-sealed">
                            {t('detail.sealed_link')}
                        </Link>
                    </p>
                )}
            </section>
            <section className="space-y-2" data-testid="signature-request-journal">
                <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('detail.journal')}</h2>
                    {chain.data && (
                        <Badge variant={chain.data.valid ? 'success' : 'destructive'} data-testid="signature-request-chain">
                            {chain.data.valid ? t('detail.chain_valid') : t('detail.chain_broken', { at: chain.data.broken_at })}
                        </Badge>
                    )}
                </div>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>{t('detail.when')}</TableHead>
                            <TableHead>{t('detail.event')}</TableHead>
                            <TableHead>{t('detail.details')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(request.events ?? []).map((e) => (
                            <TableRow key={e.uid} data-testid={`signature-event-${e.sequence}`}>
                                <TableCell>{e.sequence}</TableCell>
                                <TableCell className="whitespace-nowrap">{formatDate(e.occurred_at, { dateStyle: 'short', timeStyle: 'medium' })}</TableCell>
                                <TableCell>{t(`events.${e.type}`)}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {[
                                        request.signers.find((s) => s.uid === e.signer_uid)?.name,
                                        ...Object.entries(e.payload ?? {}).map(([k, v]) => `${k}: ${String(v)}`),
                                        e.ip,
                                    ].filter(Boolean).join(' · ')}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </section>
        </div>
    )
}
